"use client";
import React, { useContext } from "react";
import Link from "next/link";
import { locationcontext } from "@/lib/context";
import { cmsLinkUrl, toDownloadUrl } from "@/lib/downloadUrl";
import { EXTERNAL_REL } from "@/lib/externalLink";

const HAS_SCHEME = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i;

const CMSLink = ({
  config,
  className,
  disabledClassName,
  children,
  useIn = "other",
  fallbackWrapper = false,
  city,
  ...props
}) => {
  const context = useContext(locationcontext);
  const citySlug = (context?.location || city || "ahmedabad").toLowerCase();

  let cfg = config;
  while (Array.isArray(cfg)) {
    cfg = cfg[0];
  }

  if (!cfg || typeof cfg !== "object") {
    return fallbackWrapper ? <div className={className}>{children}</div> : null;
  }

  const label = children || cfg?.label || (useIn !== "header" ? "Click here" : null);

  const isNested = typeof cfg?.url === "object" && cfg?.url !== null;

  // Nested URL fields are `{ type, link }` (current admin) or `{ type, url }`
  // (older entries); cmsLinkUrl prefers `link`, the value the admin edits now.
  let href = isNested ? cmsLinkUrl(cfg.url) : cfg?.url;

  const type = isNested ? cfg?.url?.type : cfg?.type || "internal";

  // Downloads go through /api/download so the browser saves the file under
  // the CMS filename instead of opening it.
  const isDownload =
    cfg?.force_download === true ||
    (type === "download" && cfg?.force_download !== false);

  if (typeof href === "string" && !isDownload) {
    href = href.replace(/(^|\/)city(\/|$|\?|#)/, `$1${citySlug}$2`);
  }

  // Treat missing OR empty-string href the same — no usable destination
  if (!href || href.trim() === "") {
    const emptyClassName =
      [className, disabledClassName].filter(Boolean).join(" ") || undefined;
    return fallbackWrapper ? (
      <div className={emptyClassName}>{children || label}</div>
    ) : label ? (
      <span className={emptyClassName} {...props}>
        {label}
      </span>
    ) : null;
  }

  if (isDownload) {
    return (
      <a
        href={toDownloadUrl(href.trim(), config?.filename)}
        className={className}
        {...props}
      >
        {label}
      </a>
    );
  }

  // An external link typed without a scheme ("fonts.google.com/…") would
  // otherwise become an internal "/fonts.google.com/…" route.
  if (type === "external" && !HAS_SCHEME.test(href) && !href.startsWith("/")) {
    href = `https://${href}`;
  }

  const isExternal = type === "external" || href?.startsWith?.("http");

  if (isExternal) {
    return (
      <a
        href={href}
        target="_blank"
        rel={EXTERNAL_REL}
        className={className}
        {...props}
      >
        {label}
      </a>
    );
  }

  return (
    <Link
      href={href?.startsWith?.("/") ? href : `/${href}`}
      className={className}
      {...props}
    >
      {label}
    </Link>
  );
};

export default CMSLink;
